/**
 * MyActions — User's action execution history view
 *
 * Shows a rich table of the current user's action executions with handler icons,
 * inline binding context, expandable detail rows with tabs, rich result cells,
 * duration info, and auto-refresh via SSE query invalidation.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useApi } from '../lib/api';
import { fetchJson } from '../lib/apiFetchJson';
import { ActivityEntriesResponseSchema, type ActivityEntry } from '@shared/schemas/activity-entry';
import { Badge } from '../components/ui/Badge';
import { Select } from '../components/ui/Select';
import { useTrackViewViewed } from '../hooks/useTrackViewViewed';

// ─── Types ─────────────────────────────────────────────────

type HandlerResult = NonNullable<ActivityEntry['handlerResult']>;

// ─── Relative time formatting ──────────────────────────────

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const TIME_DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.35, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Infinity, unit: 'year' },
];

function relativeTime(dateStr: string): string {
  let duration = (new Date(dateStr).getTime() - Date.now()) / 1000;
  for (const division of TIME_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return dateStr;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const TABLE_COL_COUNT = 7;

// ─── Helper: truncate long IDs ─────────────────────────────

function truncateId(id: string, maxLen = 16): string {
  return id.length > maxLen ? `${id.slice(0, 8)}\u2026${id.slice(-4)}` : id;
}

// ─── Helper: cast handlerResult ────────────────────────────

function getHR(entry: ActivityEntry): HandlerResult | undefined {
  return entry.handlerResult ?? undefined;
}

// ─── Source label mapping ──────────────────────────────────

function sourceLabel(source: string): string {
  switch (source) {
    case 'artifact_card': return 'Card';
    case 'global_menu': return 'Menu';
    case 'api': return 'API';
    case 'retry': return 'Retry';
    case 'bundle_action': return 'Bundle';
    default: return source;
  }
}

function sourcePillClass(source: string): string {
  const base = 'text-[10px] px-1.5 py-px rounded-full whitespace-nowrap';
  switch (source) {
    case 'api':
      return `${base} bg-bg-orange-light text-content-warning border border-border-warning/30`;
    case 'retry':
      return `${base} bg-bg-blue-light text-content-action border border-border-action/30`;
    default:
      return `${base} bg-bg-tertiary text-content-tertiary`;
  }
}

// ─── Shared SVG icon components ───────────────────────────

function SalesforceCloudSvg({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M10.08 5.13C10.9 3.88 12.32 3.06 13.93 3.06c1.98 0 3.67 1.2 4.4 2.91a5.17 5.17 0 0 1 1.63-.27c2.83 0 5.13 2.3 5.13 5.13s-2.3 5.13-5.13 5.13c-.43 0-.85-.06-1.25-.16a4.1 4.1 0 0 1-3.66 2.26c-.63 0-1.23-.14-1.76-.4a4.62 4.62 0 0 1-4.25 2.82 4.62 4.62 0 0 1-4.5-3.6A4.1 4.1 0 0 1 1 13.01c0-1.84 1.2-3.4 2.87-3.93a4.62 4.62 0 0 1 3.58-5.58c.86-.17 1.73-.04 2.63.63Z" /></svg>;
}

function SendArrowSvg({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2" /></svg>;
}

function ExternalLinkSvg({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>;
}

// ─── Handler icon ──────────────────────────────────────────

function HandlerIcon({ handler }: { handler: string }) {
  switch (handler) {
    case 'salesforce_patch':
      return (
        <div className="w-6 h-6 rounded inline-flex items-center justify-center flex-shrink-0 bg-bg-blue-light text-content-action" title="Salesforce Patch">
          <SalesforceCloudSvg className="w-3 h-3" />
        </div>
      );
    case 'webhook':
      return (
        <div className="w-6 h-6 rounded inline-flex items-center justify-center flex-shrink-0 bg-bg-purple-light text-text-purple" title="Webhook">
          <SendArrowSvg className="w-3 h-3" />
        </div>
      );
    case 'slack':
      return (
        <div className="w-6 h-6 rounded inline-flex items-center justify-center flex-shrink-0 bg-bg-yellow text-text-yellow" title="Slack">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z" /><path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" /><path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z" /><path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z" /><path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z" /><path d="M14 19v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5S16.33 19 15.5 19H14z" /><path d="M10 9.5C10 10.33 9.33 11 8.5 11h-5C2.67 11 2 10.33 2 9.5S2.67 8 3.5 8h5c.83 0 1.5.67 1.5 1.5z" /><path d="M10 5V3.5C10 2.67 9.33 2 8.5 2S7 2.67 7 3.5 7.67 5 8.5 5H10z" /></svg>
        </div>
      );
    case 'none':
      return (
        <div className="w-6 h-6 rounded inline-flex items-center justify-center flex-shrink-0 bg-bg-tertiary text-icon-subtle text-xs" title="No handler">
          &mdash;
        </div>
      );
    default:
      return (
        <div className="w-6 h-6 rounded inline-flex items-center justify-center flex-shrink-0 bg-bg-tertiary text-icon-subtle text-xs" title={handler || 'Unknown'}>
          &mdash;
        </div>
      );
  }
}

// ─── Link icon helper ──────────────────────────────────────

function LinkIcon({ icon }: { icon?: string }) {
  const size = 'w-2.5 h-2.5';
  if (icon === 'salesforce') return <SalesforceCloudSvg className={size} />;
  if (icon === 'slack') return <SendArrowSvg className={size} />;
  return <ExternalLinkSvg className={size} />;
}

function linkClassName(icon?: string): string {
  if (icon === 'salesforce') {
    return 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium no-underline border transition-colors text-content-action border-blueberry-100 bg-bg-blue-light hover:bg-bg-blue whitespace-nowrap flex-shrink-0';
  }
  return 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium no-underline border transition-colors text-content-tertiary border-border-secondary bg-bg-raised hover:bg-bg-secondary whitespace-nowrap flex-shrink-0';
}

// ─── Outcome badge ─────────────────────────────────────────

function OutcomeBadge({ entry }: { entry: ActivityEntry }) {
  if (entry.kind === 'bound' && entry.target?.outcome?.status) {
    const raw = entry.target.outcome.status;
    const label = raw.charAt(0).toUpperCase() + raw.slice(1);
    if (raw === 'dismissed') {
      return <Badge tone="neutral" className="line-through">{label}</Badge>;
    }
    return <Badge tone="success">{label}</Badge>;
  }

  switch (entry.outcomeStatus) {
    case 'completed': return <Badge tone="success">Completed</Badge>;
    case 'pending': return <Badge tone="neutral">Pending</Badge>;
    case 'cancelled': return <Badge tone="neutral" className="line-through">Cancelled</Badge>;
    default: return <Badge tone="neutral">{entry.outcomeStatus}</Badge>;
  }
}

// ─── Status indicator ──────────────────────────────────────

function StatusCell({ entry }: { entry: ActivityEntry }) {
  switch (entry.deliveryStatus) {
    case 'delivered':
      return (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <span className="w-[6px] h-[6px] rounded-full bg-green-600 inline-block" />
          Delivered
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <span className="w-[6px] h-[6px] rounded-full bg-pomegranate-600 inline-block" />
          Failed
        </span>
      );
    case 'pending':
    case 'sent':
      return (
        <span className="inline-flex items-center gap-1.5 text-sm text-content-warning">
          <span className="w-[6px] h-[6px] rounded-full bg-amber-600 inline-block animate-pulse" />
          Sending...
        </span>
      );
    case 'skipped':
      return (
        <span className="inline-flex items-center gap-1.5 text-sm text-icon-subtle">
          <span className="w-[6px] h-[6px] rounded-full bg-bg-tertiary-hover inline-block" />
          Skipped
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 text-sm text-icon-subtle">
          <span className="w-[6px] h-[6px] rounded-full bg-bg-tertiary-hover inline-block" />
          {entry.deliveryStatus}
        </span>
      );
  }
}

// ─── Inline result cell ────────────────────────────────────

function ResultCell({ execution, onRetry }: { execution: ActivityEntry; onRetry: (exec: ActivityEntry) => void }) {
  const navigate = useNavigate();
  const hr = getHR(execution);
  const meta = hr?.meta;

  // Pending / sending
  if (execution.deliveryStatus === 'pending' || execution.deliveryStatus === 'sent') {
    const pendingLabel = execution.handler === 'salesforce_patch'
      ? 'Sending to Salesforce...'
      : execution.handler === 'webhook'
        ? 'Sending webhook...'
        : 'Processing...';
    return <span className="text-xs text-icon-subtle">{pendingLabel}</span>;
  }

  // Skipped
  if (execution.deliveryStatus === 'skipped') {
    return <span className="text-xs text-icon-subtle">&mdash;</span>;
  }

  // Failed — inline: [CODE] message [Retry]
  if (execution.deliveryStatus === 'failed') {
    const errorMsg = hr?.message || execution.errorMessage;
    return (
      <div className="flex items-center gap-2">
        {hr?.code && (
          <span className="font-mono text-[10px] px-1 py-0.5 rounded bg-bg-red-light text-content-danger">{hr.code}</span>
        )}
        <span className="text-xs text-content-danger truncate max-w-[160px]">{errorMsg ?? 'Failed'}</span>
        {meta?.links?.map((link, i) => (
          // eslint-disable-next-line react/no-array-index-key -- same URL may appear with different labels; composite with index disambiguates
          <a key={`${link.url}-${i}`} href={link.url} target="_blank" rel="noopener noreferrer" className={linkClassName(link.icon)} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <LinkIcon icon={link.icon} /> {link.label}
          </a>
        ))}
        {execution.retryable && (
          <button
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-content-action border border-blueberry-100 bg-transparent hover:bg-bg-blue-light cursor-pointer whitespace-nowrap flex-shrink-0"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRetry(execution); }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Delivered with rich meta — inline summary + links
  if (execution.deliveryStatus === 'delivered' && meta) {
    return (
      <div className="flex items-center gap-2">
        {meta.summary && <span className="text-xs text-content-tertiary truncate max-w-[200px]">{meta.summary}</span>}
        {meta.details && meta.details.length > 0 && meta.details.map((d, i) => (
          // eslint-disable-next-line react/no-array-index-key -- compact meta details; label optional and values may repeat
          <span key={`d-${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-tertiary text-content-tertiary text-[10px]">
            {d.label && <span className="text-icon-subtle">{d.label}</span>}
            {String(d.value)}
          </span>
        ))}
        {meta.links?.map((link, i) => (
          // eslint-disable-next-line react/no-array-index-key -- same URL may appear with different labels; composite with index disambiguates
          <a key={`${link.url}-${i}`} href={link.url} target="_blank" rel="noopener noreferrer" className={linkClassName(link.icon)} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <LinkIcon icon={link.icon} /> {link.label}
          </a>
        ))}
      </div>
    );
  }

  // Delivered with handlerSummary (no meta)
  if (execution.deliveryStatus === 'delivered' && execution.handlerSummary) {
    return <span className="text-xs text-content-tertiary">{execution.handlerSummary}</span>;
  }

  // Bundle with correlation — artifact count + View Bundle
  if (execution.deliveryStatus === 'delivered' && execution.correlation?.correlationId) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-content-tertiary">{execution.correlation.artifactCount} artifacts</span>
        <button
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-content-action border border-blueberry-100 bg-bg-blue-light hover:bg-bg-blue cursor-pointer whitespace-nowrap flex-shrink-0"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/?correlationId=${execution.correlation!.correlationId}`); }}
        >
          View Bundle
        </button>
      </div>
    );
  }

  // Bundle kind with bundle correlation
  if (execution.deliveryStatus === 'delivered' && execution.bundle?.correlationId) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-content-tertiary">Delivered</span>
        <button
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-content-action border border-blueberry-100 bg-bg-blue-light hover:bg-bg-blue cursor-pointer whitespace-nowrap flex-shrink-0"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/?correlationId=${execution.bundle!.correlationId}`); }}
        >
          View Bundle
        </button>
      </div>
    );
  }

  // Simple delivered
  if (execution.deliveryStatus === 'delivered') {
    return <span className="text-xs text-green-600">Delivered</span>;
  }

  return <span className="text-xs text-icon-subtle">&mdash;</span>;
}

// ─── Expand panel tabs ─────────────────────────────────────

const TAB_DETAILS = 'Details';
const TAB_INPUT = 'Input';
const TAB_PAYLOAD = 'Handler Payload';
const TAB_RESPONSE = 'Handler Response';
const TAB_ERROR = 'Error';

function getAvailableTabs(entry: ActivityEntry): string[] {
  const hr = getHR(entry);
  const tabs = [TAB_DETAILS];
  if (entry.input && Object.keys(entry.input).length > 0) tabs.push(TAB_INPUT);
  if (entry.handlerPayload) tabs.push(TAB_PAYLOAD);
  if (entry.deliveryStatus === 'failed' && hr) {
    tabs.push(TAB_ERROR);
  } else if (hr) {
    tabs.push(TAB_RESPONSE);
  }
  return tabs;
}

function ExpandPanel({ entry, activeTab, onTabChange }: { entry: ActivityEntry; activeTab: string; onTabChange: (tab: string) => void }) {
  const hr = getHR(entry);
  const meta = hr?.meta;
  const tabs = getAvailableTabs(entry);
  const durationMs = meta?.durationMs;

  return (
    <div className="bg-bg-secondary rounded-lg border border-border-secondary p-4 mt-1">
      {tabs.length > 1 && (
        <div className="flex gap-1 mb-3">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`px-2.5 py-1 text-[11px] rounded cursor-pointer border-none ${activeTab === tab ? 'bg-bg-blue-light text-content-action font-medium' : 'bg-transparent text-icon-subtle'}`}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onTabChange(tab); }}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Details tab */}
      {activeTab === TAB_DETAILS && (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.05em] uppercase text-icon-subtle mb-0.5">Execution</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <dt className="text-[11px] text-icon-subtle text-right pt-0.5">ID</dt>
              <dd className="text-xs text-content-primary m-0"><code className="text-[11px] bg-bg-tertiary px-1 rounded">{truncateId(entry.id)}</code></dd>
              <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Handler</dt>
              <dd className="text-xs text-content-primary m-0">{entry.handler}</dd>
              <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Source</dt>
              <dd className="text-xs text-content-primary m-0">{sourceLabel(entry.source)}</dd>
              {durationMs != null && (
                <>
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Duration</dt>
                  <dd className="text-xs text-content-primary m-0">{durationMs.toLocaleString()}ms</dd>
                </>
              )}
              <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Created</dt>
              <dd className="text-xs text-content-primary m-0">{new Date(entry.createdAt).toLocaleString()}</dd>
              {entry.completionStatus && (
                <>
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Completion</dt>
                  <dd className="text-xs text-content-primary m-0">
                    {entry.completionStatus === 'completed'
                      ? <span className="text-green-600">Completed{entry.correlation ? ` (${entry.correlation.artifactCount} artifacts)` : ''}</span>
                      : entry.completionStatus === 'awaiting' && entry.correlation
                        ? `Awaiting artifacts (${entry.correlation.artifactCount}/${entry.correlation.expectedArtifactCount ?? '?'} expected)`
                        : entry.completionStatus
                    }
                  </dd>
                </>
              )}
            </dl>
          </div>
          <div>
            {entry.kind === 'bound' && entry.target && (
              <>
                <div className="text-[10px] font-semibold tracking-[0.05em] uppercase text-icon-subtle mb-0.5">Binding</div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Blueprint</dt>
                  <dd className="text-xs text-content-primary m-0"><span className="text-content-action">{entry.target.blueprintDisplayName}</span></dd>
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Artifact</dt>
                  <dd className="text-xs text-content-primary m-0"><span className="text-content-action">{entry.target.artifactSummary ?? entry.target.artifactId}</span></dd>
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Artifact ID</dt>
                  <dd className="text-xs text-content-primary m-0"><code className="text-[11px] bg-bg-tertiary px-1 rounded">{truncateId(entry.target.artifactId)}</code></dd>
                  {entry.target.outcome && (
                    <>
                      <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Outcome</dt>
                      <dd className="text-xs m-0"><OutcomeBadge entry={entry} /></dd>
                    </>
                  )}
                </dl>
              </>
            )}
            {entry.kind === 'bundle' && entry.bundle && (
              <>
                <div className="text-[10px] font-semibold tracking-[0.05em] uppercase text-icon-subtle mb-0.5">Bundle details</div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Bundle kind</dt>
                  <dd className="text-xs text-content-primary m-0">{entry.bundle.bundleKind}</dd>
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Correlation</dt>
                  <dd className="text-xs text-content-primary m-0"><code className="text-[11px] bg-bg-tertiary px-1 rounded">{truncateId(entry.bundle.correlationId)}</code></dd>
                </dl>
              </>
            )}
            {entry.kind === 'unbound' && entry.correlation && (
              <>
                <div className="text-[10px] font-semibold tracking-[0.05em] uppercase text-icon-subtle mb-0.5">Correlation</div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Correlation ID</dt>
                  <dd className="text-xs text-content-primary m-0"><code className="text-[11px] bg-bg-tertiary px-1 rounded">{truncateId(entry.correlation.correlationId)}</code></dd>
                  <dt className="text-[11px] text-icon-subtle text-right pt-0.5">Artifacts received</dt>
                  <dd className="text-xs text-content-primary m-0">{entry.correlation.artifactCount}{entry.correlation.expectedArtifactCount != null ? ` / ${entry.correlation.expectedArtifactCount}` : ''}</dd>
                </dl>
              </>
            )}
          </div>
        </div>
      )}

      {/* Input tab */}
      {activeTab === TAB_INPUT && entry.input && (
        <div>
          <div className="text-[10px] font-semibold tracking-[0.05em] uppercase text-icon-subtle mb-1">User-provided input values</div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 mt-1">
            {Object.entries(entry.input).map(([key, value]) => (
              <React.Fragment key={key}>
                <dt className="text-[11px] text-icon-subtle text-right pt-0.5">{key}</dt>
                <dd className="text-xs text-content-primary m-0">{String(value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

      {/* Handler Payload tab */}
      {activeTab === TAB_PAYLOAD && entry.handlerPayload && (
        <pre className="text-[11px] bg-bg-raised border border-border-secondary rounded p-3 mt-1 overflow-x-auto text-content-tertiary">{JSON.stringify(entry.handlerPayload, null, 2)}</pre>
      )}

      {/* Handler Response tab */}
      {activeTab === TAB_RESPONSE && hr && (
        <pre className="text-[11px] bg-bg-raised border border-border-secondary rounded p-3 mt-1 overflow-x-auto text-content-tertiary">{JSON.stringify(hr, null, 2)}</pre>
      )}

      {/* Error tab */}
      {activeTab === TAB_ERROR && hr && (
        <pre className="text-[11px] bg-bg-raised border border-pomegranate-100 rounded p-3 mt-1 overflow-x-auto text-content-danger">{JSON.stringify(hr, null, 2)}</pre>
      )}
    </div>
  );
}

// ─── Action cell (inline) ──────────────────────────────────

function ActionCell({ entry }: { entry: ActivityEntry }) {
  const navigate = useNavigate();

  if (entry.kind === 'bound' && entry.target) {
    return (
      <div className="flex items-center gap-2">
        <HandlerIcon handler={entry.handler} />
        <span className="text-sm font-medium text-content-primary">{entry.actionName ?? 'Unknown'}</span>
        <span className="text-icon-subtle text-xs">on</span>
        <span className="inline-flex items-center px-1.5 py-0 rounded bg-bg-blue-light text-content-action text-[10px] font-medium">{entry.target.blueprintDisplayName}</span>
        <span className="text-icon-subtle text-xs">/</span>
        <a
          href={`/artifact/${entry.target.artifactId}`}
          className="text-xs text-content-action hover:underline truncate max-w-[180px]"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); navigate(`/artifact/${entry.target!.artifactId}`); }}
        >
          {entry.target.artifactSummary ?? entry.target.artifactId}
        </a>
      </div>
    );
  }

  if (entry.kind === 'bundle') {
    return (
      <div className="flex items-center gap-2">
        <HandlerIcon handler={entry.handler} />
        <span className="text-sm font-medium text-content-primary">{entry.actionName ?? 'Unknown'}</span>
        <span className="text-icon-subtle text-xs">on</span>
        <span className="inline-flex items-center px-1.5 py-0 rounded bg-bg-blue-light text-content-action text-[10px] font-medium">{entry.bundle?.bundleKind ?? 'Bundle'}</span>
        {entry.correlation?.artifactCount != null && (
          <span className="inline-flex items-center px-1.5 py-0 rounded-full bg-bg-tertiary text-content-tertiary text-[10px] font-medium">
            {entry.correlation.artifactCount} artifact{entry.correlation.artifactCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  }

  // Global (unbound)
  return (
    <div className="flex items-center gap-2">
      <HandlerIcon handler={entry.handler} />
      <span className="text-sm font-medium text-content-primary">{entry.actionName ?? 'Unknown'}</span>
      <span className="text-xs text-icon-subtle">global</span>
    </div>
  );
}

// ─── Section divider row ──────────────────────────────────

function SectionDivider({ label, count }: { label: string; count: number }) {
  return (
    <tr>
      <td colSpan={TABLE_COL_COUNT} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-icon-subtle bg-bg-tertiary border-b border-border-secondary">
        {label} <span className="text-icon-subtle font-normal">({count})</span>
      </td>
    </tr>
  );
}

// ─── Execution rows with expand ────────────────────────────

function ExecutionRows({
  items,
  onRetry,
  expandedRows,
  toggleRow,
  activeTabs,
  setActiveTab,
}: {
  items: ActivityEntry[];
  onRetry: (exec: ActivityEntry) => void;
  expandedRows: Set<string>;
  toggleRow: (id: string) => void;
  activeTabs: Record<string, string>;
  setActiveTab: (id: string, tab: string) => void;
}) {
  return (
    <>
      {items.map((exec) => {
        const isExpanded = expandedRows.has(exec.id);
        const meta = getHR(exec)?.meta;
        const durationMs = meta?.durationMs;

        return (
          <React.Fragment key={exec.id}>
            {/* Data row */}
            <tr
              className="border-b border-border-tertiary cursor-pointer hover:bg-bg-secondary transition-colors"
              onClick={() => toggleRow(exec.id)}
              tabIndex={0}
              role="row"
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRow(exec.id); } }}
            >
              <td className="px-2 py-3 text-icon-subtle">
                <span aria-expanded={isExpanded} role="button" className={`inline-block text-[10px] transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
              </td>
              <td className="px-3 py-3">
                <ActionCell entry={exec} />
              </td>
              <td className="px-3 py-3">
                <span className={sourcePillClass(exec.source)}>
                  {sourceLabel(exec.source)}
                </span>
              </td>
              <td className="px-3 py-3">
                <OutcomeBadge entry={exec} />
              </td>
              <td className="px-3 py-3">
                <StatusCell entry={exec} />
              </td>
              <td className="px-3 py-3">
                <ResultCell execution={exec} onRetry={onRetry} />
              </td>
              <td className="px-3 py-3 text-right whitespace-nowrap">
                <span className="text-sm text-icon-subtle">{relativeTime(exec.createdAt)}</span>
                {durationMs != null && (
                  <span className="text-[10px] text-icon-subtle ml-1">{formatDuration(durationMs)}</span>
                )}
              </td>
            </tr>

            {/* Expand row */}
            {isExpanded && (
              <tr className="border-b border-border-tertiary">
                <td></td>
                <td colSpan={TABLE_COL_COUNT - 1} className="px-3 py-0 pb-4">
                  <ExpandPanel
                    entry={exec}
                    activeTab={activeTabs[exec.id] ?? TAB_DETAILS}
                    onTabChange={(tab) => setActiveTab(exec.id, tab)}
                  />
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

// ─── Main component ────────────────────────────────────────

function MyActions() {
  useTrackViewViewed('My Actions Viewed');
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const kindFilter = searchParams.get('kind') || '';
  const deliveryFilter = searchParams.get('deliveryStatus') || '';
  const handlerFilter = searchParams.get('handler') || '';

  // Expand/tab state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});

  const toggleRow = (id: string) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedRows(next);
  };

  const setActiveTab = (id: string, tab: string) => {
    setActiveTabs({ ...activeTabs, [id]: tab });
  };

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) { next.set(key, value); } else { next.delete(key); }
    setSearchParams(next, { replace: true });
  };

  const { data: executions, isLoading, error } = useQuery<ActivityEntry[]>({
    queryKey: ['executions', { kind: kindFilter || undefined, deliveryStatus: deliveryFilter || undefined, handler: handlerFilter || undefined }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (kindFilter) params.set('kind', kindFilter);
      if (deliveryFilter) params.set('deliveryStatus', deliveryFilter);
      if (handlerFilter) params.set('handler', handlerFilter);
      return fetchJson(apiFetch, ActivityEntriesResponseSchema, `/api/v4/executions?${params.toString()}`);
    },
    staleTime: Infinity,
    refetchInterval: 30_000,
  });

  const handleRetry = useCallback(async (exec: ActivityEntry) => {
    try {
      await apiFetch(`/api/v4/executions/${exec.id}/retry-handler`, { method: 'POST' });
      void queryClient.invalidateQueries({ queryKey: ['executions'] });
    } catch {
      // Error will show on next poll refresh
    }
  }, [apiFetch, queryClient]);


  // Handler filter is now applied server-side
  const filtered = executions;

  // Split into three sections
  const artifactActions = filtered?.filter((e) => e.kind === 'bound') ?? [];
  const bundleActions = filtered?.filter((e) => e.kind === 'bundle') ?? [];
  const globalActions = filtered?.filter((e) => e.kind === 'unbound') ?? [];

  const hasAnyFilters = kindFilter || deliveryFilter || handlerFilter;

  return (
    <div className="h-full flex overflow-hidden bg-bg-secondary">
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-bg-raised border-b border-border-secondary px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <History className="w-5 h-5 text-content-tertiary" />
              <h1 className="text-lg font-semibold text-content-primary">My Actions</h1>
              {filtered && (
                <span className="text-xs text-icon-subtle ml-1">{filtered.length} execution{filtered.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Select className="w-36" value={kindFilter} onChange={(e) => setFilter('kind', e.target.value)}>
                <option value="">All Types</option>
                <option value="bound">On Artifact</option>
                <option value="bundle">Bundle</option>
                <option value="unbound">Global</option>
              </Select>
              <Select className="w-40" value={handlerFilter} onChange={(e) => setFilter('handler', e.target.value)}>
                <option value="">All Handlers</option>
                <option value="salesforce_patch">Salesforce</option>
                <option value="webhook">Webhook</option>
                <option value="slack">Slack</option>
                <option value="none">None</option>
              </Select>
              <Select className="w-36" value={deliveryFilter} onChange={(e) => setFilter('deliveryStatus', e.target.value)}>
                <option value="">All Status</option>
                <option value="delivered">Delivered</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="skipped">Skipped</option>
              </Select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-16 text-icon-subtle">
              Loading executions...
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-16 text-content-danger">
              Failed to load executions. Please try again.
            </div>
          )}

          {!isLoading && !error && filtered && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-icon-subtle">
              <History className="w-10 h-10 mb-3 text-icon-subtle" />
              <p className="text-sm">No actions found</p>
              {hasAnyFilters && (
                <button className="mt-2 text-sm text-content-action hover:underline" onClick={() => setSearchParams({}, { replace: true })}>
                  Clear filters
                </button>
              )}
            </div>
          )}

          {!isLoading && !error && filtered && filtered.length > 0 && (
            <table className="min-w-full">
              <thead>
                <tr className="bg-bg-secondary border-b border-border-secondary text-left">
                  <th className="w-6 px-2 py-2.5"></th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold text-icon-subtle uppercase tracking-wider">Action</th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold text-icon-subtle uppercase tracking-wider">Trigger</th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold text-icon-subtle uppercase tracking-wider">Outcome</th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold text-icon-subtle uppercase tracking-wider">Delivery</th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold text-icon-subtle uppercase tracking-wider">Result</th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold text-icon-subtle uppercase tracking-wider text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {artifactActions.length > 0 && (
                  <>
                    <SectionDivider label="Artifact Actions" count={artifactActions.length} />
                    <ExecutionRows
                      items={artifactActions}
                      onRetry={(exec) => { void handleRetry(exec); }}
                      expandedRows={expandedRows}
                      toggleRow={toggleRow}
                      activeTabs={activeTabs}
                      setActiveTab={setActiveTab}
                    />
                  </>
                )}
                {bundleActions.length > 0 && (
                  <>
                    <SectionDivider label="Bundle Actions" count={bundleActions.length} />
                    <ExecutionRows
                      items={bundleActions}
                      onRetry={(exec) => { void handleRetry(exec); }}
                      expandedRows={expandedRows}
                      toggleRow={toggleRow}
                      activeTabs={activeTabs}
                      setActiveTab={setActiveTab}
                    />
                  </>
                )}
                {globalActions.length > 0 && (
                  <>
                    <SectionDivider label="Global Actions" count={globalActions.length} />
                    <ExecutionRows
                      items={globalActions}
                      onRetry={(exec) => { void handleRetry(exec); }}
                      expandedRows={expandedRows}
                      toggleRow={toggleRow}
                      activeTabs={activeTabs}
                      setActiveTab={setActiveTab}
                    />
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyActions;
